import React from 'react';
import { MdHistory } from 'react-icons/md';
import { useWorkItemHistoryQuery } from 'service/project/hooks/workItem';
import { getInitials } from 'utils/getInitials';
import { ClipLoader } from 'react-spinners';
import { formatDate } from 'utils/helpers';
import { parseDate } from 'utils/date';

interface TaskHistoryProps {
  projectUuid: string;
  workItemUuid: string;
}

const TaskHistory: React.FC<TaskHistoryProps> = ({ projectUuid, workItemUuid }) => {
  const { data: history, isLoading } = useWorkItemHistoryQuery(projectUuid, workItemUuid);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <ClipLoader color="#8B5CF6" size={30} />
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-12 h-12 bg-primary-subtle rounded-full flex items-center justify-center mb-4">
          <MdHistory className="text-neutral-medium text-2xl" />
        </div>
        <h3 className="text-neutral font-medium mb-1">No Activity Yet</h3>
        <p className="text-neutral-medium text-sm max-w-sm">
          There are no changes recorded for this task yet.
        </p>
      </div>
    );
  }

  // Sort history by date descending (newest first)
  // Use lastModifiedAt or actionDate
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = parseDate(a.lastModifiedAt || a.actionDate);
    const dateB = parseDate(b.lastModifiedAt || b.actionDate);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="space-y-6 pb-2">
      {sortedHistory.map((entry, index) => (
        <div key={index} className="flex gap-4 group">
          <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-primary-subtle rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0 z-10 border border-primary-subtle">
                {getInitials(entry.actionByName || 'Unknown User')}
              </div>
             {index !== sortedHistory.length - 1 && (
               <div className="w-[1px] flex-1 bg-primary-subtle -mt-1 mb-1"></div>
             )}
          </div>
          <div className="flex-1 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-neutral text-sm">{entry.actionByName}</span>
              <span className="text-neutral-medium text-[11px]">
                {formatDate(entry.lastModifiedAt || entry.actionDate)}
              </span>
            </div>
            <div className="text-neutral-light text-[13px] leading-relaxed">
              {entry.actionDescription || 'Activity recorded'}
            </div>
            
            {(entry.workAuditTrailType === 'UPDATED_STATUS' || 
              entry.workAuditTrailType === 'UPDATED_PRIORITY' || 
              entry.workAuditTrailType === 'UPDATED_TITLE' || 
              entry.workAuditTrailType === 'UPDATED_REPORTER' || 
              entry.workAuditTrailType === 'UPDATED_ASSIGNEE' ||
              entry.actionDescription?.toLowerCase()?.includes('status updated') ||
              entry.actionDescription?.toLowerCase()?.includes('priority changed') ||
              entry.actionDescription?.toLowerCase()?.includes('assignee changed') ||
              entry.actionDescription?.toLowerCase()?.includes('reporter changed')) && 
              entry.previousStatus && entry.currentStatus && entry.previousStatus !== "NONE" && (
               <div className="mt-2 flex items-center gap-2 text-[11px]">
                 <span className="px-2 py-0.5 bg-primary-subtle/50 rounded text-neutral-medium line-through">
                   {entry.previousStatus}
                 </span>
                 <span className="text-neutral-medium">→</span>
                 <span className="px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded font-medium">
                   {entry.currentStatus}
                 </span>
               </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TaskHistory;
